import { RowSelectionState } from '@tanstack/react-table';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  batchDeleteInstantEvents, getInstantEventList, GetInstantEventListParams,
} from 'app/apis/InstantEventServices';
import { GetShopListParams, getShopList, getOneShop } from 'app/apis/ShopServices';
import {
  Container, Table, TableFilterRow, TableFooter,
} from 'app/components';
import { ToolBar } from 'app/components/Page/ToolBar';
import { PAGE_SIZE } from 'app/configs';
import { useAutoComplete, useTableData } from 'app/hooks';
import { FilterTypeOption } from 'app/models/Common';
import { INSTANT_EVENT_LIST_SORT_OPTION_MAP, InstantEventDeletedEnum, InstantEventResponse } from 'app/models/InstantEvent';
import { ShopResponse } from 'app/models/Shop';
import { useAuthStore, useShopStore } from 'app/stores';

import { InstantEventListColumns } from './InstantEventListColumns';

export const InstantEventListPage = () => {
  const [searchParams] = useSearchParams();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const { profile } = useAuthStore();
  const { shop } = useShopStore();
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const {
    dataList: data,
    count,
    loading,
    page,
    getDataList,
  } = useTableData<InstantEventResponse, GetInstantEventListParams>({
    call: getInstantEventList,
    filterVariableArray: ['asc', 'desc', 'name', 'shopId', 'startDateFrom', 'startDateTo', 'deleted'],
    initialParams: {
      limit: PAGE_SIZE,
      offset: 0,
      shopId: shop?.shopId,
    },
  });

  const {
    setSearchText: setShopText,
    dataList: shopList,
    onMenuScrollToBottom: onShopMenuScrollToBottom,
  } = useAutoComplete<GetShopListParams, ShopResponse>({
    getListCall: getShopList,
    getItemCall: (shopId) => getOneShop(Number(shopId)),
    selectedId: searchParams.get('shopId') ?? undefined,
    labelRender: (featuredShop: ShopResponse) => ({
      label: `#${featuredShop.shopId} | ${featuredShop.shopTitle}`,
      value: featuredShop.shopId,
    }),
    searchTextFieldName: 'shopTitle',
    initialParams: {
      limit: PAGE_SIZE,
      offset: 0,
      shopId: shop?.shopId,
    },
  });

  const deleteBtnOnClick = async () => {
    const keys = Object.keys(rowSelection);
    if (!keys.length) {
      toast.error('請選擇要刪除的即時活動');
      return;
    }
    const selectedInstantEvent = data.filter((_, index) => rowSelection[index]);
    try {
      setDeleteLoading(true);
      await batchDeleteInstantEvents(selectedInstantEvent.map((event) => event.instantEventId));
      // const PromiseArray = selectedInstantEvent.map(async (instantEvent) => {
      //   await deleteOneInstantEvent(instantEvent.instantEventId);
      // });
      // await Promise.all(PromiseArray);
      getDataList();
      toast.success('即時活動已被刪除');
    } catch (err) {
      toast.error('刪除失敗');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Container>
      <ToolBar
        title="即時活動列表"
        loading={deleteLoading}
        confirmDeleteMessage="確定要刪除已選擇的即時活動？"
        deleteBtnOnClick={profile?.role === '超級管理員' ? deleteBtnOnClick : undefined}
      />
      <TableFilterRow
        filterItems={[
          {
            type: FilterTypeOption.sort,
            label: '排序 ASC',
            name: 'asc',
            optionMap: INSTANT_EVENT_LIST_SORT_OPTION_MAP,
          },
          {
            type: FilterTypeOption.sort,
            label: '排序 DESC',
            name: 'desc',
            optionMap: INSTANT_EVENT_LIST_SORT_OPTION_MAP,
          },
          {
            type: FilterTypeOption.search,
            label: '標題',
            name: 'name',
            placeholder: '捜尋標題',
          },
          {
            type: FilterTypeOption.autocomplete,
            label: '商店',
            name: 'shopId',
            placeholder: '捜尋商店',
            options: shopList,
            onMenuScrollToBottom: onShopMenuScrollToBottom,
            onInputChange: setShopText,
          },
          {
            type: FilterTypeOption.date,
            label: '開始日期',
            name: { start: 'startDateFrom', end: 'startDateTo' },
            placeholder: '捜尋開始日期',
          },
          {
            type: FilterTypeOption.select,
            label: '狀態',
            name: 'deleted',
            placeholder: '現存',
            options: Object.entries(InstantEventDeletedEnum).map(([key, value]) => ({
              label: value,
              value: key,
            })),
          },
        ]}
      />
      <Table
        data={data}
        defaultColumns={InstantEventListColumns()}
        rowSelection={rowSelection}
        loading={loading}
        setRowSelection={setRowSelection}
      />
      <TableFooter count={count} page={page} />
    </Container>
  );
};
